import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, AlertCircle, Lock, Send } from 'lucide-react';
import { getApiBaseUrl } from '../../services/api';

type FormStatus = 'pending' | 'submitted' | 'expired' | 'not_found';

interface PendingForm {
  status: 'pending';
  prompt: string;
  response_type: 'text' | 'number' | 'choice' | 'scale';
  response_config?: { choices?: string[]; scale_min?: number; scale_max?: number };
  unit?: string | null;
}

interface SubmittedForm {
  status: 'submitted';
  prompt: string;
  response_type: string;
  submitted_at?: string;
}

interface ExpiredForm {
  status: 'expired';
  prompt: string;
}

type FormData = PendingForm | SubmittedForm | ExpiredForm;

/**
 * Public, no-auth page that renders a tokenized workflow form.
 *
 * Mounted at /f/:token. The token IS the auth — single-use, expires after
 * 72h by default. PHI-flagged ask steps land here instead of being asked
 * over WhatsApp directly.
 */
export const SecureFormPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<FormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [value, setValue] = useState<string>('');

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await axios.get<FormData>(`${getApiBaseUrl()}/public/forms/${token}`);
        setData(res.data);
      } catch (err: any) {
        if (err?.response?.status === 404) {
          setError('This link is invalid or has been removed.');
        } else {
          setError(err?.response?.data?.detail || 'Failed to load form.');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !data || data.status !== 'pending') return;

    let parsed: any = value.trim();
    if (data.response_type === 'number' || data.response_type === 'scale') {
      const num = Number(parsed);
      if (Number.isNaN(num)) {
        setError('Please enter a number.');
        return;
      }
      parsed = num;
    }

    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${getApiBaseUrl()}/public/forms/${token}/submit`, { value: parsed });
      setSubmitted(true);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Header />

        {loading && (
          <Card>
            <div className="text-center py-6 text-gray-500 text-sm">
              <Loader2 size={20} className="animate-spin inline-block mr-2" />
              Loading…
            </div>
          </Card>
        )}

        {error && !loading && (
          <Card>
            <ErrorBlock message={error} />
          </Card>
        )}

        {!loading && data && data.status === 'submitted' && !submitted && (
          <Card>
            <SuccessBlock title="Already submitted" body="Thanks — we've recorded your response." />
          </Card>
        )}

        {!loading && data && data.status === 'expired' && (
          <Card>
            <ErrorBlock message="This link has expired. Please ask for a new one." />
          </Card>
        )}

        {submitted && (
          <Card>
            <SuccessBlock title="Got it ✓" body="Your response has been securely recorded." />
          </Card>
        )}

        {!loading && data && data.status === 'pending' && !submitted && (
          <Card>
            <form onSubmit={submit} className="space-y-5">
              <div>
                <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">
                  <Lock size={11} /> Secure
                </span>
                <h2 className="text-lg font-semibold text-gray-900 mt-3 leading-snug">{data.prompt}</h2>
                {data.unit && (
                  <p className="text-xs text-gray-500 mt-1">Unit: {data.unit}</p>
                )}
              </div>

              <FormInput
                value={value}
                onChange={setValue}
                responseType={data.response_type}
                config={data.response_config}
              />

              <button
                type="submit"
                disabled={submitting || !value.trim()}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 whitespace-nowrap"
              >
                {submitting ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                {submitting ? 'Submitting…' : 'Submit'}
              </button>

              <p className="text-[11px] text-gray-400 text-center leading-snug">
                This form is encrypted in transit. Your response is sent to your provider's
                dashboard, not over WhatsApp.
              </p>
            </form>
          </Card>
        )}
      </div>
    </div>
  );
};

const Header: React.FC = () => (
  <div className="text-center mb-6">
    <div className="inline-flex items-center gap-1.5 text-emerald-600 text-xs font-semibold tracking-wide uppercase">
      <Lock size={12} />
      Secure check-in
    </div>
  </div>
);

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">{children}</div>
);

const ErrorBlock: React.FC<{ message: string }> = ({ message }) => (
  <div className="text-center py-4">
    <div className="w-10 h-10 rounded-full bg-red-50 mx-auto mb-3 flex items-center justify-center">
      <AlertCircle size={20} className="text-red-500" />
    </div>
    <p className="text-sm text-gray-700 leading-relaxed">{message}</p>
  </div>
);

const SuccessBlock: React.FC<{ title: string; body: string }> = ({ title, body }) => (
  <div className="text-center py-6">
    <div className="w-12 h-12 rounded-full bg-emerald-50 mx-auto mb-3 flex items-center justify-center">
      <CheckCircle2 size={24} className="text-emerald-600" />
    </div>
    <h2 className="font-semibold text-gray-900 mb-1">{title}</h2>
    <p className="text-sm text-gray-500">{body}</p>
  </div>
);

const FormInput: React.FC<{
  value: string;
  onChange: (v: string) => void;
  responseType: PendingForm['response_type'];
  config?: PendingForm['response_config'];
}> = ({ value, onChange, responseType, config }) => {
  const inputCls =
    'w-full px-3 py-3 rounded-xl border border-gray-200 text-base focus:outline-none focus:ring-2 focus:ring-emerald-200';

  if (responseType === 'choice') {
    const choices = config?.choices || [];
    return (
      <div className="space-y-2">
        {choices.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => onChange(c)}
            className={`w-full text-left px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all capitalize ${
              value === c
                ? 'bg-emerald-50 border-emerald-300 text-emerald-700 ring-2 ring-emerald-100'
                : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
            }`}
          >
            {c}
          </button>
        ))}
      </div>
    );
  }

  if (responseType === 'scale') {
    const min = config?.scale_min ?? 1;
    const max = config?.scale_max ?? 10;
    const nums = Array.from({ length: max - min + 1 }, (_, i) => min + i);
    return (
      <div className="grid grid-cols-5 gap-2">
        {nums.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(String(n))}
            className={`py-3 rounded-xl border-2 text-sm font-medium transition-all ${
              value === String(n)
                ? 'bg-emerald-50 border-emerald-300 text-emerald-700 ring-2 ring-emerald-100'
                : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
            }`}
          >
            {n}
          </button>
        ))}
      </div>
    );
  }

  if (responseType === 'number') {
    return (
      <input
        type="number"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter a number"
        className={inputCls}
        autoFocus
      />
    );
  }

  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Type your answer…"
      rows={4}
      className={inputCls}
      autoFocus
    />
  );
};

export default SecureFormPage;
